import { useEffect, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import InputGroup from '../InputGroup/InputGroup';
import Modal from '../Modal/Modal';
import ToggleSwitch from '../ToggleSwitch/ToggleSwitch';

const TopFieldConfigModal = ({ isOpen, onClose, field, onSave, showToast }) => {
  const [config, setConfig] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [newOptionValue, setNewOptionValue] = useState('');
  const [optionError, setOptionError] = useState(false);

  useEffect(() => {
    if (field && isOpen) {
      setConfig(field.config || {});
    }
  }, [field, isOpen]);

  if (!field) return null;

  const handleConfigChange = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const handleAddOption = () => {
    if (!newOptionValue.trim()) {
      setOptionError(true);
      showToast?.('error', __('Error', 'havenlytics'), __('Option value is required', 'havenlytics'));
      return;
    }
    
    setOptionError(false);

    const currentOptions = config.options || [];
    setConfig(prev => ({
      ...prev,
      options: [...currentOptions, newOptionValue.trim()]
    }));
    setNewOptionValue('');
  };

  const handleRemoveOption = (index) => {
    const currentOptions = [...(config.options || [])];
    currentOptions.splice(index, 1);
    setConfig(prev => ({ ...prev, options: currentOptions }));
  };

  const handleUpdateOption = (index, value) => {
    const currentOptions = [...(config.options || [])];
    currentOptions[index] = value;
    setConfig(prev => ({ ...prev, options: currentOptions }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updatedField = { ...field, config: config };
      await onSave(updatedField);
      onClose();
    } catch (error) {
      console.error('Failed to save field config:', error);
      showToast?.('error', __('Error', 'havenlytics'), __('Failed to save field configuration', 'havenlytics'));
    } finally {
      setIsSaving(false);
    }
  };

  const renderConfigContent = () => {
    switch (field.type) {
      case 'range':
        return renderRangeConfig();
      case 'number':
        return renderNumberConfig();
      default:
        return (
          <div className="hvnly-top-field-config__info">
            <i className="fas fa-info-circle"></i>
            <p>{__('This field type does not require additional configuration.', 'havenlytics')}</p>
          </div>
        );
    }
  };

  const renderRangeConfig = () => {
    return (
      <div className="hvnly-top-field-config">
        <div className="hvnly-top-field-config__section">
          <h4>{__('Price Range Settings', 'havenlytics')}</h4>
          
          <InputGroup
            label={__('Placeholder', 'havenlytics')}
            id="placeholder"
            value={config.placeholder || __('Any', 'havenlytics')}
            onChange={(value) => handleConfigChange('placeholder', value)}
          />
          
          <div className="hvnly-top-field-config__options-list">
            <label className="hvnly-top-field-config__label">{__('Options', 'havenlytics')}</label>
            <div className="hvnly-top-field-config__options-group">
              {(config.options || []).map((opt, idx) => (
                <div key={idx} className="hvnly-top-field-config__option-item">
                  <input 
                    type="text" 
                    className="hvnly-top-field-config__option-input" 
                    value={opt} 
                    onChange={(e) => handleUpdateOption(idx, e.target.value)} 
                  />
                  <button type="button" className="hvnly-top-field-config__option-remove" onClick={() => handleRemoveOption(idx)}>
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              ))}
              <div className="hvnly-top-field-config__add-option-form">
                <input
                  type="text"
                  className={`hvnly-top-field-config__option-input ${optionError ? 'hvnly-top-field-config__option-input--error' : ''}`}
                  placeholder={__('Add Option', 'havenlytics')}
                  value={newOptionValue}
                  onChange={(e) => {
                    setNewOptionValue(e.target.value);
                    if (optionError) setOptionError(false);
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddOption();
                    }
                  }}
                />
                <button type="button" className="hvnly-top-field-config__add-option" onClick={handleAddOption}>
                  <i className="fas fa-plus"></i> {__('Add Option', 'havenlytics')}
                </button>
              </div>
            </div>
          </div>

          <ToggleSwitch
            id="usePresetValues"
            checked={config.usePresetValues !== false}
            onChange={(checked) => handleConfigChange('usePresetValues', checked)}
            label={__('Use Preset Values (Dropdown instead of input)', 'havenlytics')}
          />
        </div>
      </div>
    );
  };

  const renderNumberConfig = () => {
    return (
      <div className="hvnly-top-field-config">
        <div className="hvnly-top-field-config__section">
          <h4>{__('Number Field Settings', 'havenlytics')}</h4>
          
          <InputGroup label={__('Minimum Value', 'havenlytics')} id="min" type="number" value={config.min || 1} onChange={(value) => handleConfigChange('min', parseInt(value, 10))} />
          <InputGroup label={__('Maximum Value', 'havenlytics')} id="max" type="number" value={config.max || 10} onChange={(value) => handleConfigChange('max', parseInt(value, 10))} />
          <InputGroup label={__('Placeholder', 'havenlytics')} id="placeholder" value={config.placeholder || __('Any', 'havenlytics')} onChange={(value) => handleConfigChange('placeholder', value)} />

          <div className="hvnly-top-field-config__options-list">
            <label className="hvnly-top-field-config__label">{__('Preset Options', 'havenlytics')}</label>
            <div className="hvnly-top-field-config__options-group">
              {(config.options || []).map((opt, idx) => (
                <div key={idx} className="hvnly-top-field-config__option-item">
                  <input 
                    type="text" 
                    className="hvnly-top-field-config__option-input" 
                    value={opt} 
                    onChange={(e) => handleUpdateOption(idx, e.target.value)} 
                  />
                  <button type="button" className="hvnly-top-field-config__option-remove" onClick={() => handleRemoveOption(idx)}>
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              ))}
              <div className="hvnly-top-field-config__add-option-form">
                <input
                  type="text"
                  className={`hvnly-top-field-config__option-input ${optionError ? 'hvnly-top-field-config__option-input--error' : ''}`}
                  placeholder={__('Add Option', 'havenlytics')}
                  value={newOptionValue}
                  onChange={(e) => {
                    setNewOptionValue(e.target.value);
                    if (optionError) setOptionError(false);
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddOption();
                    }
                  }}
                />
                <button type="button" className="hvnly-top-field-config__add-option" onClick={handleAddOption}>
                  <i className="fas fa-plus"></i> {__('Add Option', 'havenlytics')}
                </button>
              </div>
            </div>
          </div>

          <ToggleSwitch
            id="usePresetValues"
            checked={config.usePresetValues !== false}
            onChange={(checked) => handleConfigChange('usePresetValues', checked)}
            label={__('Use Preset Values (Dropdown instead of input)', 'havenlytics')}
          />
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={handleSave}
      title={`${__('Configure', 'havenlytics')}: ${field.title}`}
      size="large"
      confirmText={__('Save Configuration', 'havenlytics')}
      isLoading={isSaving}
    >
      <div className="hvnly-top-field-config-modal-content">
        {renderConfigContent()}
      </div>
    </Modal>
  );
};

export default TopFieldConfigModal;